import { Suspense, lazy, useState, useEffect, useRef, Component, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useEnabledApps } from "../../hooks/useEnabledApps";
import { isAppEnabled } from "../../lib/appAccess";
import { useMochiSuppressed } from "./useMochiSuppressed";
import { useAssistantConfig } from "./useAssistantConfig";
import { useAssistantProfile, useUpdateAssistantProfile } from "../../modules/assistant/hooks/useAssistantProfile";
import { MochiFallbackButton } from "./MochiFallbackButton";
import { MochiOfferBubble } from "./MochiOfferBubble";
import { MochiPopover } from "./popover/MochiPopover";
import { MochiEntrance } from "./entrance/MochiEntrance";
import { useAssistantMoodPublisher, useAmbientPublisher } from "./publishers";
import { useDockDrag } from "./useDockDrag";
import { useMochiClearance } from "./mochiClearance";

const MochiScene = lazy(() => import("./MochiScene"));

const MOCHI_POPOVER_ID = "mochi-popover";

/**
 * Where the dock sits, and how its children line up inside it.
 *
 * Kept apart because a drag replaces the anchor (the corner) but must NOT
 * replace the alignment: forcing `items-start` on a right-hand dock re-aligned
 * the bubble and the avatar against each other, so the very first nudge made
 * everything visibly jump sideways.
 */
const PLACEMENT: Record<string, { anchor: string; align: string }> = {
  "bottom-right": { anchor: "bottom-4 right-4", align: "items-end" },
  "bottom-left": { anchor: "bottom-4 left-4", align: "items-start" },
  "top-right": { anchor: "top-4 right-4", align: "items-end" },
  "top-left": { anchor: "top-4 left-4", align: "items-start" },
};

/** Renders the 2D fallback if the lazy scene import or WebGL throws. */
class SceneBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export function MochiOverlay() {
  const { t } = useTranslation();
  useAssistantMoodPublisher();
  useAmbientPublisher();
  const enabled = useEnabledApps();
  const suppressed = useMochiSuppressed();
  const cfg = useAssistantConfig();
  const rawProfile = useAssistantProfile();
  const update = useUpdateAssistantProfile();
  const [open, setOpen] = useState(false);
  const dockRef = useRef<HTMLDivElement>(null);
  const dock = useDockDrag(dockRef, cfg.avatar_placement);
  // The avatar itself, not the dock: an open popover grows the dock upwards,
  // and footers must not jump about when it does.
  const [avatarEl, setAvatarEl] = useState<HTMLDivElement | null>(null);
  useMochiClearance(avatarEl, `${cfg.avatar_placement}|${dock.at?.x},${dock.at?.y}`);

  const [entering, setEntering] = useState(false);
  // RULING 1: "not onboarded" reads the raw profile query, not the resolved
  // config — the resolved block always fills in a default name/avatar even
  // before the user has ever touched onboarding.
  const onboarded = Boolean(rawProfile.data?.onboarding_dismissed);

  useEffect(() => {
    const openIt = () => setOpen(true);
    window.addEventListener("weco:mochi:open", openIt);
    return () => window.removeEventListener("weco:mochi:open", openIt);
  }, []);

  useEffect(() => {
    const enterCenter = () => setEntering(true);
    window.addEventListener("weco:mochi:enter-center", enterCenter);
    return () => window.removeEventListener("weco:mochi:enter-center", enterCenter);
  }, []);

  // One-shot migration: legacy localStorage hide -> profile mochi_enabled=false.
  useEffect(() => {
    if (localStorage.getItem("weco.mochi.hidden") === "1") {
      update.mutate({ mochi_enabled: false });
      try {
        localStorage.removeItem("weco.mochi.hidden");
      } catch {
        /* ignore */
      }
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isAppEnabled(enabled, "dashboard")) return null; // assistant app off
  if (suppressed || cfg.mochi_enabled === false) return null;

  const placement = PLACEMENT[cfg.avatar_placement] ?? PLACEMENT["bottom-right"];
  const launcherAriaLabel = onboarded
    ? t("mochi.talkTo", { name: cfg.assistant_name })
    : t("mochi.meetCompanion");
  const fallback = (
    <MochiFallbackButton
      onClick={() => {
        if (dock.consumeClickSuppression()) return;
        setOpen((v) => !v);
      }}
      avatar={cfg.avatar}
      hue={cfg.avatar_hue}
      ariaLabel={launcherAriaLabel}
      ariaExpanded={open}
      ariaControls={MOCHI_POPOVER_ID}
    />
  );

  // Dragged somewhere? Then its own position wins and the corner classes have
  // to go -- leaving `bottom-4 right-4` on would fight the inline left/top and
  // stretch the dock across the screen.
  const dragged = dock.at !== null;
  return (
    <div
      ref={dockRef}
      className={`fixed z-[60] flex flex-col gap-2 ${placement.align} ${
        dragged ? "" : placement.anchor
      }`}
      style={{
        pointerEvents: "none",
        ...(dock.at ? { left: dock.at.x, top: dock.at.y } : null),
      }}
    >
      {entering ? (
        // While the center-stage entrance is up, suppress the corner scene/
        // popover/bubble entirely so there is never a second Mochi (and a
        // second GL context) on screen at once.
        <MochiEntrance onDocked={() => setEntering(false)} />
      ) : (
        <>
          {/* Merge resolution: a not-yet-onboarded user sees the naming nudge
              (ours); once onboarded, the contextual offer bubble (theirs). This
              avoids stacking two bubbles — revisit if offers should show pre-naming. */}
          {!open &&
            (!onboarded ? (
              <div className="hidden sm:block" style={{ pointerEvents: "none" }}>
                <div className="max-w-[220px] rounded-2xl border border-border bg-background/90 backdrop-blur-md px-3 py-2 text-sm text-foreground shadow-xl">
                  {t("mochi.nameBubble")}
                </div>
              </div>
            ) : (
              <MochiOfferBubble />
            ))}
          {open && (
            <div id={MOCHI_POPOVER_ID} style={{ pointerEvents: "auto" }}>
              <MochiPopover
                onClose={() => setOpen(false)}
                onHideMochi={() => {
                  setOpen(false);
                  update.mutate({ mochi_enabled: false });
                }}
              />
            </div>
          )}
          <div
            ref={setAvatarEl}
            style={{
              pointerEvents: "auto",
              // While dragging, the cursor should say so and the browser must
              // not try to pan/scroll the page under the finger.
              cursor: dock.dragging ? "grabbing" : "grab",
              touchAction: "none",
            }}
            {...dock.handlers}
          >
            <SceneBoundary fallback={fallback}>
              <Suspense fallback={fallback}>
                <MochiScene
                  onClick={() => {
                    if (dock.consumeClickSuppression()) return;
                    setOpen((v) => !v);
                  }}
                  avatar={cfg.avatar}
                  palette={cfg.palette}
                  size={cfg.avatar_size}
                  hue={cfg.avatar_hue}
                  ariaLabel={launcherAriaLabel}
                  expanded={open}
                  controls={MOCHI_POPOVER_ID}
                />
              </Suspense>
            </SceneBoundary>
          </div>
        </>
      )}
    </div>
  );
}
